import { useColorMode } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import { clearModuleRefusal } from '@/libs/license'
import type { IProperty } from '@/types/Property'
import type { IRoom } from '@/types/Room'
import type { IUser } from '@/types/User'

export interface LoggedInUserData {
    id:       string
    name:     string
    username: string

    // What this account may do, as answered by GET /me. Every screen branches on this
    // and never on a role name: a button that always comes back 403 is a lie, so it is
    // not drawn (docs/PRAVICE.md §1).
    permissions: string[]
}

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'sl')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<LoggedInUserData>()

    // The property whose rooms this phone is cleaning. Same key and same meaning as in
    // the reception portal — a housekeeper works one house per shift, so it is chosen
    // once and remembered rather than asked for on every screen.
    const propertyId = ref(localStorage.getItem('propertyId') || '')

    const properties = ref<IProperty[]>([])

    // The rooms of that property, kept by id: a task carries a room_id and nothing
    // else, so without this map the sheet is a list of ObjectIDs. Loaded once per
    // session rather than per screen — the inventory does not change during a shift.
    const rooms = ref<Record<string, IRoom>>({})

    // Set when the API refuses the property or room list to this account (403 —
    // both endpoints are guarded by property:read, which a housekeeper does not
    // hold). The screens read it to say what is missing instead of rendering an
    // empty list that looks like a quiet morning.
    const propertiesRestricted = ref(false)
    const roomsRestricted      = ref(false)

    // The accounts a task can be handed to, kept by id. Loaded only when the account
    // holds user:read — GET /users is guarded by it and a supervisor need not hold it.
    // When it is empty because the API refused, usersRestricted says so and the sheet
    // hides assignment altogether rather than offering a control that always 403s.
    const users           = ref<Record<string, IUser>>({})
    const usersRestricted = ref(false)

    // Whether GET /me and the property resolution have been done for this token. The
    // router waits on it, so no screen renders before it knows what it may offer.
    const sessionLoaded = ref(false)

    // Which modules this hotel holds, as GET /modules answers them.
    //
    // # Why this portal needs the list at all, having never needed it before
    //
    // Because of "Moj urnik" and "Moja ura", which read the rota and the working-time
    // record — and both belong to `staff`, a DIFFERENT licence from the `operations`
    // this portal is the face of (docs/MODULI.md, docs/OSEBJE.md §14). Every other
    // screen here is `operations`, so the portal could always assume: if it got in at
    // all, the module was on.
    //
    // The consequence of guessing wrong is not a blank tab, it is the whole portal.
    // A 403 carrying code 19 sets libs/license.ts's flag from stores/fetch.ts — the
    // one place every call passes through — and App.vue then draws the locked notice
    // over everything. So a house that runs housekeeping and has not bought the rota
    // would lose its ROOMS the moment somebody's phone asked for a week. The tabs are
    // therefore never drawn and the routes never reached unless this list says `staff`,
    // and GET /modules is the one read that cannot produce that refusal: it is
    // unguarded by design and answers what the licence holds.
    //
    // Empty and unloaded are the same thing here on purpose, and the direction is the
    // opposite of the reception portal's "unknown means yes": an unknown licence hides
    // the extra tabs rather than showing them. The permission mask already hides every
    // other menu entry for a module the house lacks; this is the same answer for a
    // screen that has no permission to be masked.
    const modules = ref<string[]>([])

    /**
     * How many proposals the assistant has executed since this page was loaded.
     *
     * Milo is a drawer over whatever screen is open (libs/assistant.ts), so a
     * confirmation in it changes the page behind it — the round it just handed to
     * somebody else, the week it just reshuffled. A screen that goes on showing what it
     * read before the change is a screen the supervisor stops believing.
     *
     * A counter and not an event, for the reason `onAssistantExecuted` gives: a screen
     * mounted after the execution has already read the new data and must not reload for
     * it. It is here rather than in libs/assistant.ts because the views watch it, and a
     * view already holds this store.
     */
    const assistantExecutions = ref(0)

    /** Called by the assistant once a proposal has actually run. */
    const assistantExecuted = () => assistantExecutions.value++

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
    // never a second source of truth. Values: 'auto' | 'light' | 'dark'.
    //
    // emitAuto is what makes 'auto' readable. Without it VueUse resolves the stored
    // preference before handing it over, so an account set to 'auto' on a device whose
    // system is dark reads back as 'dark' — and the three-state button in the topbar
    // could never leave: it saw 'dark', wrote 'auto', read 'dark' again, and a
    // housekeeper on a dark iPad pressed it all day for nothing. On a light device the
    // same code appeared to work, because there 'auto' reads back as 'light' and the
    // cycle at least moved. The class on <html> still follows the RESOLVED mode; only
    // what we read here is the preference itself.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme', emitAuto: true })

    window.addEventListener('resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    /** What this account may do. Unknown until GET /me has answered, so it is false. */
    const can = (permission: string) => !!userData.value?.permissions?.includes(permission)

    /**
     * Whether this hotel holds a module. Unknown until GET /modules has answered, so
     * it is false — a tab that is missing for a second is better than a tab that locks
     * the portal when it is pressed. See `modules` above.
     */
    const hasModule = (key: string) => modules.value.includes(key)

    /**
     * The house this phone is standing in, out of the list the session loaded.
     *
     * undefined where the id names nothing we hold — an account with no assignment at
     * all, or a remembered house that is no longer ours. Callers say what unknown means
     * for them rather than being handed a made-up property: the list is sometimes the
     * restricted one from GET /me, which carries an id and a name and no settings at
     * all (libs/session.ts).
     */
    const getProperty = () => properties.value.find(property => property.id === propertyId.value)

    const getRoom       = (roomId: string) => rooms.value[roomId]
    const getRoomNumber = (roomId: string) => rooms.value[roomId]?.number ?? ''
    const getUserName   = (userId: string) => users.value[userId]?.name ?? ''

    /**
     * Signing out, and the cookie the server holds.
     *
     * The session cookie is written by the server and is HttpOnly, so this page cannot
     * remove it — which is the whole point of it being HttpOnly, and the reason signing
     * out has to ask the server. It is asked and not waited on: whatever the network
     * says, this browser is signed out the moment somebody presses the button.
     *
     * It is the one call in this portal that does NOT go through the fetch store, for
     * the reason CLAUDE.md deviation 16 gives about the reception portal's three: the
     * fetch store reads its token FROM this store, so a call made from here through it
     * is a cycle. Spelled `window.fetch` to name the platform primitive on purpose —
     * the same spelling and the same rule as there, and nothing else here may do it.
     */
    const clearServerSession = () => {
        void window.fetch('/api/pms/v1/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
    }

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt

        // A refusal was recorded for whoever was signed in before. This is somebody
        // else — or the same person after the hotel bought the module — so the portal
        // asks again rather than showing them the previous account's locked door.
        clearModuleRefusal()
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        userData.value = undefined
        properties.value = []
        rooms.value = {}
        users.value = {}
        propertiesRestricted.value = false
        roomsRestricted.value = false
        usersRestricted.value = false
        sessionLoaded.value = false
        modules.value = []

        // Signing out ends the only identity the refusal was about. Leaving it set
        // would greet the next person at this tablet with a locked door that was never
        // theirs, and the login screen underneath would never be reached.
        clearModuleRefusal()

        clearServerSession()
    }

    const setPropertyId = (id: string) => {
        propertyId.value = id
        localStorage.setItem('propertyId', id)
    }

    const setUserData   = (data: LoggedInUserData | undefined) => userData.value = data
    const setProperties = (list: IProperty[]) => properties.value = list
    const setModules    = (list: string[]) => modules.value = list

    const setRooms = (list: IRoom[]) => {
        rooms.value = Object.fromEntries(list.map(room => [room.id, room]))
    }

    const setUsers = (list: IUser[]) => {
        users.value = Object.fromEntries(list.map(user => [user.id, user]))
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
    }

    return {
        JWT, locale, userData, loaderActive, colorMode, propertyId, properties, rooms, users, modules,
        assistantExecutions,
        propertiesRestricted, roomsRestricted, usersRestricted, sessionLoaded,
        getJWT, isLoggedIn, isLoaderActive, isMobile, can, hasModule,
        getProperty, getRoom, getRoomNumber, getUserName,
        setLogin, setLogout, setPropertyId, setUserData, setProperties, setModules, setRooms, setUsers,
        startLoading, stopLoading, changeLocale, assistantExecuted,
    }
})
